// Import the router
import { useRouter } from 'next/router'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode
} from 'react'
// Import cookies
import Cookies from 'js-cookie'
import client from '@config/client'
import routes from '@config/routes'
// Import PostHog
import { usePostHog } from 'posthog-js/react'

import {
  getMyProfile,
  getMyOrganization,
  getAllOrganizations,
  getMyPermissions,
  getOrgUser
} from '@api/auth'
import { obtain2faToken, unverify2fa } from '@api/profiles'

import Loader from '@components/common/Loader/Loader'
import { getOrganization } from '@api/organizations'
import { getTeam } from '@api/teams'
import { getOperationDetails } from '@api/operations'

// Define interfaces for the auth context data
interface User {
  id: string | number
  email: string
  role?: 'tenant' | string
  tenantId?: string | number
  first_name?: string
  last_name?: string
  avatar?: string
  [key: string]: any
}

interface Organization {
  id: string | number
  name: string
  currency?: string
  currency_symbol?: string
  country?: string
  smtp_enabled?: boolean
  can_add_users?: boolean
  finance_enabled?: boolean
  is_active_subscription?: boolean
  invoice_sending_enabled?: boolean
  slug?: string
  pk?: string | number
  created_at?: string
  [key: string]: any
}

interface OrgSettings {
  id: string | number
  pk: string | number
  created_at: string
  name: string
  currency?: string
  currency_symbol?: string
  country?: string
  smtp_enabled?: boolean
  can_add_users?: boolean
  finance_enabled?: boolean
  is_active_subscription?: boolean
  invoice_sending_enabled?: boolean
  slug?: string
  [key: string]: any
}

interface Team {
  id: string | number
  [key: string]: any
}

interface Membership {
  orguser: string | number
  username: string
  first_name: string
  last_name: string
  has_access: boolean
  is_staff: boolean
  is_readonly: boolean
}

interface ProfilePreferences {
  locale?: string
  two_factors_auth_activated?: boolean
  two_factors_auth_verified?: boolean
  [key: string]: any
}

interface OperationDetails {
  id: string | number
  [key: string]: any
}

interface OrganizationResponse {
  selected_org: string | number
  selected_team?: string | number
  locale?: string
  is_staff?: boolean
  domain_exists?: boolean
  two_factors_auth_activated?: boolean
  [key: string]: any
}

// Define the API response for organizations
interface OrganizationsResponse {
  data: {
    organizations: Array<{
      id: string | number
      orguser: {
        pk: string | number
        first_name: string
        last_name: string
        has_access: boolean
      }
      [key: string]: any
    }>
    username: string
    [key: string]: any
  }
  status?: number
}

// Define the API response for profile
interface ProfileResponse {
  data: User
  status?: number
}

interface AuthContextType {
  isAuthenticated: boolean
  user: User | null
  organization: string | number | null
  setOrganization: (org: string | number) => void
  organizationTeam: string | number | null
  setOrganizationTeam: (team: string | number) => void
  orgSettings: OrgSettings | null
  teamSettings: Team | null
  membership: Membership | null
  permissions: any | null
  loading: boolean
  login: (response: { data: { access: string } }, data?: any) => Promise<void>
  finishLogin: () => Promise<void>
  logout: () => Promise<void>
  updateOrganization: () => void
  _setOrgSettings: () => Promise<void>
  _setTeamSettings: () => Promise<void>
  profilePreferences: ProfilePreferences | null
  setProfilePreferences: (prefs: ProfilePreferences) => void
  viewAs: string | null
  isFinnishedStepClicked: boolean
  setIsFinnishedStepClicked: (value: boolean) => void
  operationDetails: OperationDetails | null
  updateOperationDetails: () => Promise<void>
}

// Create the auth context with default values
const AuthContext = createContext<AuthContextType | undefined>(undefined)

const isProtectedRoute = (path: string): boolean => {
  if (
    path === routes.account.login ||
    path === routes.account.loginOTP ||
    path === routes.account.signup ||
    path.includes(routes.account.resetPassword) ||
    path.includes(routes.account.activation) ||
    path.includes(routes.account.registration) ||
    path.includes(routes.account.acceptInvitation)
    // path.includes('/accept_invite')
  ) {
    return false
  } else {
    return true
  }
}

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const router = useRouter()
  const [user, setUser] = useState<User | null>(null)
  const [organization, setOrganization] = useState<string | number | null>(null)
  const [viewAs, setViewAs] = useState<string | null>(null)
  const [organizationTeam, setOrganizationTeam] = useState<
    string | number | null
  >(null)
  const [profilePreferences, setProfilePreferences] =
    useState<ProfilePreferences | null>(null)
  const [permissions, setPermissions] = useState<any | null>(null)
  const [orgSettings, setOrgSettings] = useState<OrgSettings | null>(null)
  const [teamSettings, setTeamSettings] = useState<Team | null>(null)
  const [membership, setMembership] = useState<Membership | null>(null)
  const [isFinnishedStepClicked, setIsFinnishedStepClicked] =
    useState<boolean>(false)
  const [operationDetails, setOperationDetails] =
    useState<OperationDetails | null>(null)
  const posthog = usePostHog()

  const [update, setUpdate] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(true)

  const { project_id, id } = router.query
  const operationId = id ? id : project_id

  useEffect(() => {
    if (user) {
      posthog?.identify(String(user.id), {
        email: user.email
      })
      posthog?.group('company', String(orgSettings?.pk), {
        name: orgSettings?.name
      })
    }
  }, [posthog, user, orgSettings])

  const redirectToCreateOrganization =
    async (): Promise<OrganizationResponse | null> => {
      setLoading(true)
      const organizationInfo =
        (await getMyOrganization()) as unknown as OrganizationResponse
      const organizations =
        (await getAllOrganizations()) as unknown as OrganizationsResponse

      if (organizationInfo) {
        const isOrganizationActive = organizationInfo.selected_org
          ? organizationInfo
          : organizations?.data?.organizations[0]

        if (
          organizations &&
          organizations.data &&
          organizations.data.organizations
        ) {
          const _orguserInfo = organizations.data.organizations.find(
            o => o.id == organizationInfo.selected_org
          )
          if (_orguserInfo) {
            const orguserInfo = _orguserInfo.orguser
            setMembership({
              orguser: orguserInfo.pk,
              username: organizations.data.username,
              first_name: orguserInfo.first_name,
              last_name: orguserInfo.last_name,
              has_access: orguserInfo.has_access,
              is_staff: organizationInfo?.is_staff || false,
              is_readonly: false
            })
          }
        }

        if (!isOrganizationActive && !organizationInfo?.domain_exists) {
          return null
        } else if (!isOrganizationActive && organizationInfo.domain_exists) {
          return {
            ...organizationInfo,
            selected_org: organizationInfo.selected_org || ''
          }
        } else {
          return isOrganizationActive as OrganizationResponse
        }
      }
      return null
    }

  const tenantSetup = async (): Promise<void> => {
    const org = await redirectToCreateOrganization()

    if (org && org.selected_org) {
      setOrganization(org.selected_org)
      setOrganizationTeam(org?.selected_team)
      setProfilePreferences({
        locale: org.locale
      })

      if (org.selected_team) {
        const teamInfo = await getTeam(org.selected_team)
        if (teamInfo) setTeamSettings(teamInfo.data)
      }
      setLoading(false)

      router.push(routes.tenant.root)
    }
  }

  const setParameters = async (
    usr?: User,
    fromLogin = false
  ): Promise<void> => {
    const org = await redirectToCreateOrganization()
    if (org && org.selected_org) {
      setOrganization(org.selected_org)
      setOrganizationTeam(org?.selected_team)
      setProfilePreferences({
        locale: org.locale
      })

      if (org.selected_team) {
        const teamInfo = await getTeam(org.selected_team)
        if (teamInfo) setTeamSettings(teamInfo.data)
      }
      setLoading(false)
      if (fromLogin) router.push(routes.root) // Redirect to /dashboard
    } else {
      if (fromLogin && !org?.domain_exists) {
        setLoading(false)
        router.push({
          pathname: routes.account.company,
          query: { email: user ? user.email : usr?.email }
        })
      } else if (fromLogin && org?.domain_exists) {
        setLoading(false)
        router.push(routes.account.domainExists)
      }
    }
  }

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search)
    const view_as = queryParams.get('view_as') || Cookies.get('view_as')
    if (view_as) {
      if (view_as === 'null') {
        Cookies.remove('view_as')
        Cookies.remove('view_as_display')
        setViewAs(null)
      } else {
        Cookies.set('view_as', view_as)
        setViewAs(Cookies.get('view_as_display'))
      }
    } else setViewAs(null)

    async function loadUserFromCookies() {
      const token = Cookies.get('accessToken')
      // const refreshToken = Cookies.get('refreshToken')
      if (token) {
        client.defaults.headers.Authorization = `JWT ${token}`
        const response = (await getMyProfile()) as unknown as ProfileResponse

        if (Cookies.get('view_as')) {
          const responsePermission = await getMyPermissions()
          setPermissions(responsePermission)
        }

        if (response) {
          if (response.status === 401) {
            Cookies.remove('accessToken')
            Cookies.remove('refreshToken')
            setUser(null)
            setOrganization(null)
            delete client.defaults.headers.Authorization
            router.push(routes.account.login)
          } else {
            const { data: orgUser } = await getOrgUser()
            if (orgUser.tenant) {
              setUser({
                ...response.data,
                id: response.data.id || 0,
                role: 'tenant',
                tenantId: orgUser.tenant.id
              })
            } else {
              setUser({
                ...response.data,
                id: response.data.id || 0
              })
            }
            await setParameters()
          }
        }
      }
      setLoading(false)
    }
    loadUserFromCookies()
  }, [])

  const updateOrganization = () => setUpdate(true)

  useEffect(() => {
    const organizationCheck = async () => await setParameters()
    if (update) {
      organizationCheck()
      setUpdate(false)
    }
  }, [update])

  const _setTeamSettings = async (): Promise<void> => {
    if (!organizationTeam) return
    const teamInfo = await getTeam(organizationTeam)
    if (teamInfo) setTeamSettings(teamInfo.data)
  }

  const _setOrgSettings = async (): Promise<void> => {
    if (!organization) return
    const response = await getOrganization(organization)

    if (response?.status === 200) {
      const org = response.data
      setOrgSettings({
        id: org.id,
        pk: org.id,
        created_at: org.created_at,
        name: org.name,
        currency: org.currency,
        currency_symbol: org.currency_symbol,
        country: org.country,
        smtp_enabled: org.smtp_enabled,
        can_add_users: org.can_add_users,
        finance_enabled: org.finance_enabled,
        is_active_subscription: org.is_active_subscription,
        invoice_sending_enabled: org.invoice_sending_enabled,
        slug: org.slug
      })
    }
  }

  useEffect(() => {
    if (organization) _setOrgSettings()
  }, [organization])

  const login = async (
    response: { data: { access: string } },
    data?: any
  ): Promise<void> => {
    Cookies.set('accessToken', response.data.access, {
      expires: 60
    })
    // Cookies.set('refreshaccess', response.data.refresh)
    client.defaults.headers.Authorization = `JWT ${response.data.access}`
    const organization =
      (await getMyOrganization()) as unknown as OrganizationResponse
    if (organization?.two_factors_auth_activated) {
      await obtain2faToken()
      router.push(routes.account.loginOTP)
    } else await finishLogin()
  }

  const finishLogin = async (): Promise<void> => {
    setLoading(true)

    const { data: user } = (await getMyProfile()) as unknown as { data: User }
    const { data: orgUser } = await getOrgUser()
    if (orgUser.tenant) {
      setUser({
        ...user,
        id: user.id || 0,
        role: 'tenant',
        tenantId: orgUser.tenant.id
      })
    } else {
      setUser({
        ...user,
        id: user.id || 0
      })
    }
    const responsePermission = await getMyPermissions()
    setPermissions(responsePermission)

    if (orgUser.tenant) {
      await tenantSetup()
    } else {
      await setParameters(
        {
          ...user,
          id: user.id || 0
        },
        true
      )
    }
  }

  const logout = async (): Promise<void> => {
    Cookies.remove('accessToken')
    // Cookies.remove('refreshToken')
    if (profilePreferences?.two_factors_auth_activated) await unverify2fa()
    setUser(null)
    setOrganization(null)
    setOrganizationTeam(null)
    setProfilePreferences(null)
    delete client.defaults.headers.Authorization
    router.push(routes.account.login)
  }

  useEffect(() => {
    if (user?.role === 'tenant') {
      router.push(routes.tenant.root)
    }
  }, [user])

  const isAuthenticated = useMemo(() => {
    return (
      !!user &&
      ((profilePreferences?.two_factors_auth_activated &&
        profilePreferences?.two_factors_auth_verified) ||
        !profilePreferences?.two_factors_auth_activated)
    )
  }, [user, profilePreferences])

  const updateOperationDetails = useCallback(async (): Promise<void> => {
    if (isAuthenticated && organization && operationId) {
      const operation = await getOperationDetails(
        typeof operationId === 'string' ? operationId : operationId[0]
      )
      setOperationDetails(operation)
    }
  }, [isAuthenticated, organization, operationId])

  useEffect(() => {
    updateOperationDetails()
  }, [updateOperationDetails])

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        organization,
        setOrganization,
        organizationTeam,
        setOrganizationTeam,
        orgSettings,
        teamSettings,
        membership,
        permissions,
        loading,
        login,
        finishLogin,
        logout,
        updateOrganization,
        _setOrgSettings,
        _setTeamSettings,
        profilePreferences,
        setProfilePreferences,
        viewAs,
        isFinnishedStepClicked,
        setIsFinnishedStepClicked,
        operationDetails,
        updateOperationDetails
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

// Custom hook to use the auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface ProtectRouteProps {
  children: ReactNode
}

export const ProtectRoute: React.FC<ProtectRouteProps> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth()
  const router = useRouter()

  useEffect(() => {
    if (!loading && !isAuthenticated && isProtectedRoute(router.pathname)) {
      router.push(routes.account.login)
    }
  }, [loading, isAuthenticated, router])

  if (loading) return <Loader />

  if (!isProtectedRoute(router.pathname)) {
    return <>{children}</>
  }

  if (isAuthenticated) {
    return <>{children}</>
  }

  return <Loader />
}
